// iot-admin/client/src/pages/MomentsKeyList.js
// Moments API Key management — fleet-style list view with stat cards,
// search, filters, sortable table, and pagination.
// CPO spec: No tenant dropdown. Show all keys. Tenant shown in table.
// Rotate/revoke only on detail page. Listing is read-only.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { momentsAdminService, tenantService } from '../services/api';
import authClient from '../auth/authClient';
import {
  Key, Plus, RefreshCw, Search, Shield, XCircle,
  Building2, ArrowUpDown, ChevronUp, ChevronDown,
  Zap, AlertTriangle, Copy,
  X, BarChart3, Activity, Globe,
} from 'lucide-react';
import './MomentsKeyList.css';

/* ── tiny helpers ── */
const fmt = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtRel = (d) => {
  if (!d) return 'Never';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'Now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h}h`;
  return `${Math.floor(ms / 86400000)}d`;
};
const fullDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const copyText = (text) => navigator.clipboard.writeText(text).catch(() => {});

/* ── Badge ── */
const Badge = ({ color, children }) => (
  <span className={`mk-badge mk-badge--${color}`}>{children}</span>
);

/* ── Stat card ── */
const StatCard = ({ icon: Icon, label, value, color, active, onClick }) => (
  <button className={`mk-stat ${active ? 'mk-stat--active' : ''}`} onClick={onClick} title={`Filter: ${label}`}>
    <Icon size={18} className={`mk-stat-icon mk-stat-icon--${color}`} />
    <span className="mk-stat-val">{value}</span>
    <span className="mk-stat-lbl">{label}</span>
  </button>
);

/* ── Sortable header ── */
const SortTh = ({ field, label, sorting, onSort, align }) => {
  const active = sorting.sortBy === field;
  return (
    <th className={`mk-th ${active ? 'mk-th--active' : ''} ${align === 'right' ? 'mk-th--right' : ''}`} onClick={() => onSort(field)}>
      <span className="mk-th-inner">
        {label}
        {active
          ? (sorting.sortOrder === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
          : <ArrowUpDown size={12} className="mk-th-sort-idle" />}
      </span>
    </th>
  );
};

/* ── Plan config ── */
const PLAN_META = {
  explorer: { color: 'blue', label: 'Explorer', limit: '1K/day', price: 'Free' },
  growth: { color: 'green', label: 'Growth', limit: '50K/day', price: '₹2.50/1K' },
  enterprise: { color: 'purple', label: 'Enterprise', limit: 'Unlimited', price: 'Custom' },
};

/* ── Status config ── */
const STATUS_META = {
  active: { color: 'green', icon: Shield },
  revoked: { color: 'red', icon: XCircle },
  expired: { color: 'yellow', icon: AlertTriangle },
};

/* ── Create Key Modal ── */
const CreateKeyModal = ({ tenants, onClose, onCreated }) => {
  const currentUser = authClient.getUser();
  const [form, setForm] = useState({
    name: '',
    description: '',
    plan: 'explorer',
    selectedTenant: tenants.length === 1 ? tenants[0].id : '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.selectedTenant) { setError('Please select a tenant'); return; }
    setLoading(true); setError(null);
    try {
      const data = await momentsAdminService.createKey(form.selectedTenant, {
        name: form.name,
        description: form.description,
        plan: form.plan,
      });
      setResult({ ...data.data, tenantId: form.selectedTenant });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to create key');
    } finally {
      setLoading(false);
    }
  };

  const selectedTenantName = tenants.find(t => t.id === form.selectedTenant)?.name || form.selectedTenant;

  if (result) {
    return (
      <div className="mk-modal-overlay" onClick={() => { onCreated(); onClose(); }}>
        <div className="mk-modal" onClick={e => e.stopPropagation()}>
          <div className="mk-modal-header">
            <h2>Key Created Successfully</h2>
            <button className="mk-btn-icon" onClick={() => { onCreated(); onClose(); }}><X size={18} /></button>
          </div>
          <div className="mk-modal-body">
            <div className="mk-alert mk-alert--warning">
              <AlertTriangle size={14} />
              <strong>Store this key now — it cannot be retrieved again.</strong>
            </div>
            <div className="mk-secret-display">
              <code>{result.api_key}</code>
              <button className="mk-btn mk-btn--sm" onClick={() => copyText(result.api_key)}><Copy size={12} /> Copy</button>
            </div>
            <div className="mk-key-meta">
              <span>Prefix: <strong>{result.key_prefix}…</strong></span>
              <span>Plan: <Badge color={PLAN_META[result.plan]?.color || 'blue'}>{result.plan}</Badge></span>
              <span>Tenant: <strong>{selectedTenantName}</strong></span>
            </div>
          </div>
          <div className="mk-modal-footer">
            <button className="mk-btn mk-btn--primary" onClick={() => { onCreated(); onClose(); }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mk-modal-overlay" onClick={onClose}>
      <div className="mk-modal" onClick={e => e.stopPropagation()}>
        <div className="mk-modal-header">
          <h2>Create API Key</h2>
          <button className="mk-btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mk-modal-body">
            {error && <div className="mk-alert mk-alert--error">{error}</div>}
            <div className="mk-form-group">
              <label>Tenant *</label>
              <select value={form.selectedTenant} onChange={e => setForm(f => ({ ...f, selectedTenant: e.target.value }))} required>
                <option value="">— Select tenant —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <span className="mk-form-hint">The key will be scoped to this tenant's data access</span>
            </div>
            <div className="mk-form-group">
              <label>Key Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Production Key" required minLength={2} maxLength={128} />
            </div>
            <div className="mk-form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description…" rows={2} />
            </div>
            <div className="mk-form-group">
              <label>Plan</label>
              <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                <option value="explorer">Explorer — 1K req/day, free tier</option>
                <option value="growth">Growth — 50K req/day, ₹2.50/1K</option>
                {currentUser?.isSuperAdmin && <option value="enterprise">Enterprise — Unlimited, custom pricing</option>}
              </select>
            </div>
          </div>
          <div className="mk-modal-footer">
            <button type="button" className="mk-btn mk-btn--secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="mk-btn mk-btn--primary" disabled={loading || !form.name || !form.selectedTenant}>
              {loading ? 'Creating…' : <><Plus size={14} /> Create Key</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   Main component
   ════════════════════════════════════════════════════════ */
const MomentsKeyList = () => {
  const currentUser = authClient.getUser();
  const isSuperAdmin = currentUser?.isSuperAdmin;

  const [tenants, setTenants] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  // Sorting
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });

  // Load tenants (for create modal)
  useEffect(() => {
    (async () => {
      try {
        const res = await tenantService.getAll({ type: 'organization' });
        setTenants(Array.isArray(res.data) ? res.data : res.data?.tenants || []);
      } catch (err) {
        console.error('Failed to load tenants', err);
      }
    })();
  }, []);

  // Load all keys
  const fetchKeys = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      let data;
      if (isSuperAdmin) {
        data = await momentsAdminService.listAllKeys();
      } else {
        const tenantId = currentUser?.personalTenantId;
        if (!tenantId) { setKeys([]); setLoading(false); return; }
        data = await momentsAdminService.listKeys(tenantId);
      }
      setKeys(data.data?.keys || []);
    } catch (err) {
      if (!silent) setError(err.response?.data?.error?.message || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, currentUser?.personalTenantId]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Clear messages
  useEffect(() => {
    if (message || error) { const t = setTimeout(() => { setMessage(null); setError(null); }, 5000); return () => clearTimeout(t); }
  }, [message, error]);

  // Handlers
  const handleSort = (field) => setSorting(s => ({ sortBy: field, sortOrder: s.sortBy === field && s.sortOrder === 'asc' ? 'desc' : 'asc' }));
  const handleFilter = (field, value) => {
    if (field === 'status') setStatusFilter(value);
    if (field === 'plan') setPlanFilter(value);
  };

  // Client-side filter + sort
  const filteredKeys = useMemo(() => {
    let list = [...keys];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(k =>
        (k.name || '').toLowerCase().includes(q) ||
        (k.key_prefix || '').toLowerCase().includes(q) ||
        (k.description || '').toLowerCase().includes(q) ||
        (k.tenant_name || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) list = list.filter(k => k.status === statusFilter);
    if (planFilter) list = list.filter(k => k.plan === planFilter);

    const { sortBy, sortOrder } = sorting;
    list.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === 'created_at' || sortBy === 'last_used_at') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [keys, search, statusFilter, planFilter, sorting]);

  // Stats
  const stats = useMemo(() => {
    const total = keys.length;
    const active = keys.filter(k => k.status === 'active').length;
    const revoked = keys.filter(k => k.status === 'revoked').length;
    const explorer = keys.filter(k => k.plan === 'explorer').length;
    const growth = keys.filter(k => k.plan === 'growth').length;
    const enterprise = keys.filter(k => k.plan === 'enterprise').length;
    const totalReqs = keys.reduce((sum, k) => sum + Number(k.total_requests || 0), 0);
    return { total, active, revoked, explorer, growth, enterprise, totalReqs };
  }, [keys]);

  // Active filter chips
  const activeFilters = [];
  if (statusFilter) activeFilters.push({ key: 'status', label: `Status: ${statusFilter}` });
  if (planFilter) activeFilters.push({ key: 'plan', label: `Plan: ${planFilter}` });

  return (
    <div className="mk-page">
      {/* ── Header ── */}
      <div className="mk-header">
        <div className="mk-header-left">
          <Globe size={24} className="mk-header-icon" />
          <div>
            <h1 className="mk-title">Moments API Keys</h1>
            <p className="mk-subtitle">
              {stats.total} key{stats.total !== 1 ? 's' : ''}
              {stats.totalReqs > 0 && <> · {fmt(stats.totalReqs)} requests (30d)</>}
            </p>
          </div>
        </div>
        <div className="mk-header-right">
          <button className="mk-btn mk-btn--primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New Key
          </button>
        </div>
      </div>

      {message && <div className="mk-alert mk-alert--success mk-page-alert">{message}</div>}
      {error && <div className="mk-alert mk-alert--error mk-page-alert">{error}</div>}

      {/* ── Stat strip ── */}
      <div className="mk-stats">
        <StatCard icon={Key} label="Total" value={stats.total} color="blue"
          active={!statusFilter && !planFilter} onClick={() => { setStatusFilter(''); setPlanFilter(''); }} />
        <StatCard icon={Shield} label="Active" value={stats.active} color="green"
          active={statusFilter === 'active'} onClick={() => handleFilter('status', statusFilter === 'active' ? '' : 'active')} />
        <StatCard icon={XCircle} label="Revoked" value={stats.revoked} color="red"
          active={statusFilter === 'revoked'} onClick={() => handleFilter('status', statusFilter === 'revoked' ? '' : 'revoked')} />
        <StatCard icon={Zap} label="Explorer" value={stats.explorer} color="blue"
          active={planFilter === 'explorer'} onClick={() => handleFilter('plan', planFilter === 'explorer' ? '' : 'explorer')} />
        <StatCard icon={BarChart3} label="Growth" value={stats.growth} color="teal"
          active={planFilter === 'growth'} onClick={() => handleFilter('plan', planFilter === 'growth' ? '' : 'growth')} />
        <StatCard icon={Activity} label="Enterprise" value={stats.enterprise} color="purple"
          active={planFilter === 'enterprise'} onClick={() => handleFilter('plan', planFilter === 'enterprise' ? '' : 'enterprise')} />
      </div>

      {/* ── Search ── */}
      <div className="mk-toolbar">
        <div className="mk-search-wrap">
          <Search size={16} className="mk-search-icon" />
          <input
            className="mk-search"
            type="text"
            placeholder="Search key name, prefix, tenant, description…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="mk-search-clear" onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        <button className="mk-btn mk-btn--secondary" onClick={() => fetchKeys()} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'mk-spin' : ''} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="mk-chips">
          {activeFilters.map(f => (
            <button key={f.key} className="mk-chip" onClick={() => handleFilter(f.key, '')}>
              {f.label} <X size={12} />
            </button>
          ))}
          <button className="mk-chip mk-chip--clear" onClick={() => { setStatusFilter(''); setPlanFilter(''); }}>
            Clear all
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {loading && keys.length === 0 ? (
        <div className="mk-loading"><div className="mk-spinner" /> Loading API keys…</div>
      ) : filteredKeys.length === 0 ? (
        <div className="mk-empty">
          <Key size={40} />
          <p>{keys.length === 0 ? 'No API keys yet' : 'No keys match your filters'}</p>
          <span className="mk-empty-hint">
            {keys.length === 0 ? 'Create one to get started' : 'Try adjusting search or filters'}
          </span>
        </div>
      ) : (
        <div className="mk-table-wrap">
          <table className="mk-table">
            <thead>
              <tr>
                <th className="mk-th">Status</th>
                <SortTh field="key_prefix" label="Key" sorting={sorting} onSort={handleSort} />
                <SortTh field="name" label="Name" sorting={sorting} onSort={handleSort} />
                <SortTh field="tenant_name" label="Tenant" sorting={sorting} onSort={handleSort} />
                <th className="mk-th">Plan</th>
                <SortTh field="daily_quota" label="Daily Limit" sorting={sorting} onSort={handleSort} align="right" />
                <SortTh field="created_at" label="Created" sorting={sorting} onSort={handleSort} />
                <SortTh field="last_used_at" label="Last Used" sorting={sorting} onSort={handleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {filteredKeys.map(k => {
                const sm = STATUS_META[k.status] || STATUS_META.active;
                const pm = PLAN_META[k.plan] || PLAN_META.explorer;
                const StatusIcon = sm.icon;
                return (
                  <tr key={k.id} className={`mk-row ${k.status === 'revoked' ? 'mk-row--revoked' : ''}`}>
                    <td className="mk-td">
                      <Badge color={sm.color}><StatusIcon size={11} /> {k.status}</Badge>
                    </td>
                    <td className="mk-td">
                      <Link to={`/moments/${k.id}?tenant=${k.tenant_id}`} className="mk-key-link">
                        {k.key_prefix}…
                      </Link>
                    </td>
                    <td className="mk-td">
                      <Link to={`/moments/${k.id}?tenant=${k.tenant_id}`} className="mk-name-link">
                        {k.name}
                      </Link>
                      {k.description && <span className="mk-desc">{k.description.substring(0, 60)}</span>}
                    </td>
                    <td className="mk-td">
                      <span className="mk-tenant-name">
                        <Building2 size={12} /> {k.tenant_name || k.tenant_id?.substring(0, 8) + '…'}
                      </span>
                    </td>
                    <td className="mk-td">
                      <Badge color={pm.color}>{pm.label}</Badge>
                    </td>
                    <td className="mk-td mk-td--right">
                      <span className="mk-metric-val">{k.daily_quota ? fmt(k.daily_quota) : '∞'}</span>
                    </td>
                    <td className="mk-td" title={fullDate(k.created_at)}>
                      {fmtRel(k.created_at)}
                    </td>
                    <td className="mk-td mk-td--right" title={k.last_used_at ? fullDate(k.last_used_at) : 'Never'}>
                      {k.last_used_at ? fmtRel(k.last_used_at) : <span className="mk-muted">Never</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Summary footer ── */}
      {filteredKeys.length > 0 && (
        <div className="mk-footer">
          <span>{filteredKeys.length} key{filteredKeys.length !== 1 ? 's' : ''} shown</span>
          {filteredKeys.length !== keys.length && <span className="mk-muted"> (of {keys.length} total)</span>}
        </div>
      )}

      {/* ── Create Modal ── */}
      {showCreate && (
        <CreateKeyModal
          tenants={tenants}
          onClose={() => setShowCreate(false)}
          onCreated={() => fetchKeys()}
        />
      )}
    </div>
  );
};

export default MomentsKeyList;
